import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Bell, ClipboardCheck, Package, Truck, Database, BarChart2,
  Warehouse, Settings, LogOut, ChevronDown, Store, Menu, X, DollarSign
} from 'lucide-react';
import { useAuth } from 'context/AuthContext';
import { useStore } from 'context/StoreContext';
import { NotificationBell } from './NotificationBell';

const navGroups = [
  {
    label: null,
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/alerts', icon: Bell, label: 'Profit Leaks' },
      { to: '/audit', icon: ClipboardCheck, label: 'Margin Audit' },
      { to: '/products', icon: Package, label: 'Products' },
      { to: '/costs', icon: DollarSign, label: 'Costs & Invoices' },
      { to: '/reports', icon: BarChart2, label: 'Reports' },
      { to: '/inventory', icon: Warehouse, label: 'Inventory' },
    ],
  },
  {
    label: 'Setup',
    items: [
      { to: '/data', icon: Database, label: 'Store Data' },
      { to: '/suppliers', icon: Truck, label: 'Suppliers' },
      { to: '/settings', icon: Settings, label: 'Settings' },
    ],
  },
];

function TrialBanner() {
  const { org } = useStore();
  if (!org) return null;
  const daysLeft = org.trial_ends_at ? Math.ceil((new Date(org.trial_ends_at).getTime() - Date.now()) / 86400000) : 0;
  let message: string | null = null;
  let urgent = false;
  if (org.subscription_status === 'trial') {
    if (daysLeft > 7) return null;
    urgent = daysLeft <= 0;
    message = daysLeft > 0 ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in your free trial.` : 'Your free trial has ended.';
  } else if (org.subscription_status === 'past_due') {
    urgent = true; message = "Your last payment didn't go through.";
  } else if (org.subscription_status === 'canceled') {
    urgent = true; message = 'Your subscription is canceled.';
  }
  if (!message) return null;
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm ${urgent ? 'bg-amber-100 text-amber-900' : 'bg-blue-50 text-blue-900'}`}>
      <span>{message}</span>
      {org.role === 'owner' && <NavLink to="/settings/billing" className="font-semibold underline">{org.subscription_status === 'past_due' ? 'Update payment' : 'Subscribe'}</NavLink>}
    </div>
  );
}

function StoreSwitcher() {
  const { stores, currentStore, setCurrentStore } = useStore();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-md bg-blue-800 hover:bg-blue-700 text-white text-sm transition-colors"
      >
        <Store size={14} />
        <span className="flex-1 text-left truncate">{currentStore?.name ?? 'Select store'}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          {stores.map(s => (
            <button
              key={s.id}
              onClick={() => { setCurrentStore(s); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${currentStore?.id === s.id ? 'text-blue-600 font-medium' : 'text-gray-700'}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ onClose }: { onClose?: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="flex flex-col h-full bg-blue-900 text-white">
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-blue-800">
        <div>
          <img src="/PellvrfullLogotrans_opposite.png" alt="Pellvr" className="h-8 w-auto object-contain object-left" />
          <p className="text-xs text-blue-300">Profit Intelligence</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-blue-300 hover:text-white lg:hidden">
            <X size={20} />
          </button>
        )}
      </div>

      {/* Store switcher */}
      <div className="px-3 py-3 border-b border-blue-800">
        <StoreSwitcher />
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto">
        {navGroups.map(group => (
          <div key={group.label ?? 'main'} className={group.label ? 'mt-5' : ''}>
            {group.label && <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-blue-400">{group.label}</p>}
            <div className="space-y-0.5">
              {group.items.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive ? 'bg-blue-700 text-white' : 'text-blue-200 hover:bg-blue-800 hover:text-white'
                    }`
                  }
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User */}
      <div className="px-3 py-3 border-t border-blue-800">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
            <p className="text-xs text-blue-300 truncate">{user?.email}</p>
          </div>
          <button onClick={handleLogout} className="ml-2 p-1.5 rounded hover:bg-blue-800 text-blue-300 hover:text-white">
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-56 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative flex flex-col w-56 h-full">
            <SidebarContent onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="print:hidden"><TrialBanner /></div>
        {/* Mobile topbar */}
        <div className="lg:hidden print:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
          <button onClick={() => setMobileOpen(true)} className="text-gray-600">
            <Menu size={20} />
          </button>
          <img src="/PellvrLogotrans.png" alt="Pellvr" className="h-7 w-auto object-contain" />
          <div className="ml-auto"><NotificationBell /></div>
        </div>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="mb-4 hidden justify-end lg:flex print:hidden"><NotificationBell /></div>
          {children}
        </main>
      </div>
    </div>
  );
}

